import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAttention } from '../../attention';
import { Field } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import StatusPill from '../../components/StatusPill';
import { ConfirmDialog, ReasonDialog } from '../../components/Dialog';
import { formatDateTime, formatRelative } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * Brand applications — approve or reject.
 *
 * This page is the reason the rebuild started. `routes/partners.py` has
 * shipped list/detail/approve/reject since Phase 3 and is registered in
 * main.py, but nothing in the frontend ever referenced it: a brand could
 * complete the five-step self-serve onboarding at partner.mahi.fashion, land
 * in `pending_review`, and sit there indefinitely polling
 * /onboarding/status while no one on the team had any way to see them. A
 * complete backend with no UI is a blocked business workflow, not a missing
 * chart.
 *
 * Approving is not a toggle. It adds a domain to the crawler and queues a
 * job against a third party's live website, so the page shows the full
 * application — GST, signatory, contacts, and the probe result the brand
 * passed to get here — next to the decision, rather than asking someone to
 * approve a row in a list.
 */
const STATUS_FILTERS = [
  { key: 'pending_review', label: 'Pending review' },
  { key: 'active', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

function Address({ address }) {
  if (!address) return null;
  const parts = [address.line1, address.line2, address.city, address.state, address.pincode, address.country];
  return <>{parts.filter(Boolean).join(', ')}</>;
}

export default function ApplicationsPage() {
  const { refresh: refreshAttention } = useAttention();
  const { sort, onSort } = useSort();

  const [status, setStatus] = useState('pending_review');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const previewAsBrand = async () => {
    if (!selected) return;
    setPreviewing(true);
    try {
      const { data } = await adminApi.post(`/partners/${encodeURIComponent(selected)}/impersonate`);
      window.open(data.impersonate_url, '_blank', 'noopener,noreferrer');
      toast.success(`Preview opened for ${data.brand_name} — expires in 1 hour`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not generate preview link'));
    } finally {
      setPreviewing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/partners', { params: status ? { status } : {} });
      setRows(data.partners || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load applications'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // The list row carries enough to triage; the detail call is what fetches
  // GST and signatory contact, so it happens only when someone opens one.
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    adminApi.get(`/partners/${encodeURIComponent(selected)}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) toast.error(errMsg(err, 'Could not load application')); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selected]);

  const approve = async () => {
    try {
      const { data } = await adminApi.post(`/partners/${encodeURIComponent(selected)}/approve`);
      // The backend deliberately does not roll back an approval when the
      // crawl enqueue fails — it returns a warning instead, because the
      // account really is active and the crawl can be retriggered by hand.
      // Surfacing that distinction is the difference between "done" and
      // "done, now go do one more thing".
      if (data.warning) toast(data.warning, { icon: '⚠️', duration: 8000 });
      else toast.success('Approved. Crawl queued for their site.');
      setApproving(false);
      setSelected(null);
      await Promise.all([load(), refreshAttention()]);
    } catch (err) {
      toast.error(errMsg(err, 'Could not approve'));
      setApproving(false);
    }
  };

  const reject = async (reason) => {
    try {
      await adminApi.post(`/partners/${encodeURIComponent(selected)}/reject`, { reason });
      toast.success('Application rejected.');
      setRejecting(false);
      setSelected(null);
      await Promise.all([load(), refreshAttention()]);
    } catch (err) {
      toast.error(errMsg(err, 'Could not reject'));
      setRejecting(false);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'brand_name',
      label: 'Brand',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-ink">{r.brand_name}</div>
          <div className="truncate text-[11px] text-ink-muted">{r.brand_domain}</div>
        </div>
      ),
    },
    { key: 'status', label: 'Status', sortable: true, render: (r) => <StatusPill status={r.status} /> },
    {
      key: 'test_crawl_status',
      label: 'Crawl probe',
      render: (r) => (r.test_crawl_status ? <StatusPill status={r.test_crawl_status} /> : null),
    },
    {
      key: 'created_at',
      label: 'Applied',
      sortable: true,
      render: (r) => <span title={formatDateTime(r.created_at)}>{formatRelative(r.created_at)}</span>,
    },
    {
      key: 'open',
      label: '',
      align: 'right',
      render: () => <span className="text-[11px] font-medium text-raspberry-600">Review →</span>,
    },
  ], []);

  const pending = rows.filter((r) => r.status === 'pending_review').length;

  // Client-side search + pagination (all 352 brands are already in memory)
  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) =>
        (r.brand_name || '').toLowerCase().includes(q) ||
        (r.brand_domain || '').toLowerCase().includes(q)
      )
    : rows;
  const sorted = useMemo(() => applySort(filtered, sort, columns), [filtered, sort, columns]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title="Brand applications"
        hint="Brands that applied through partner.mahi.fashion. Approving adds their domain to the crawler and queues a first crawl."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* Search */}
        <input
          type="search"
          placeholder="Search brand name or domain…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); setSelected(null); }}
          className="rounded-lg border border-hairline bg-surface px-3 py-1.5 text-xs text-ink placeholder-ink-muted focus:outline-none focus:ring-1 focus:ring-raspberry-300 w-56"
        />

        {/* Status filters */}
        <div className="flex items-center gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              onClick={() => { setStatus(f.key); setPage(0); setSelected(null); }}
              className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                status === f.key ? 'bg-raspberry-50 text-raspberry-700' : 'text-ink-secondary hover:bg-surface-page hover:text-ink'
              }`}
            >
              {f.label}
              {f.key === 'pending_review' && pending > 0 && ` (${pending})`}
            </button>
          ))}
        </div>

        {/* Result count */}
        {!loading && (
          <span className="ml-auto text-xs text-ink-muted">
            {sorted.length} brand{sorted.length !== 1 ? 's' : ''}
            {q ? ` matching "${q}"` : ''}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.partner_id}
        loading={loading}
        sort={sort}
        onSort={onSort}
        onRowClick={(r) => setSelected(r.partner_id)}
        empty={
          <EmptyState
            title={status === 'pending_review' ? 'No applications waiting' : 'Nothing here'}
            detail={
              status === 'pending_review'
                ? 'Every brand that applied has been reviewed. New applications appear here and raise a badge on the Catalog tab.'
                : 'No partner accounts match this filter.'
            }
          />
        }
      />

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-ink-muted">
          <span>
            Page {safePage + 1} of {totalPages} · {sorted.length} total
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(0)}
              disabled={safePage === 0}
              className="rounded px-2 py-1 hover:bg-surface-page disabled:opacity-30"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="rounded px-2 py-1 hover:bg-surface-page disabled:opacity-30"
            >
              ‹
            </button>
            {/* Page number buttons — show up to 5 around current */}
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter((i) => Math.abs(i - safePage) <= 2)
              .map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  className={`rounded px-2.5 py-1 font-medium ${
                    i === safePage
                      ? 'bg-raspberry-600 text-white'
                      : 'hover:bg-surface-page text-ink-secondary'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage === totalPages - 1}
              className="rounded px-2 py-1 hover:bg-surface-page disabled:opacity-30"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages - 1)}
              disabled={safePage === totalPages - 1}
              className="rounded px-2 py-1 hover:bg-surface-page disabled:opacity-30"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Detail modal — overlays the table so it's visible regardless of scroll position */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-end bg-ink/20 p-0"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="h-full w-full max-w-lg overflow-y-auto bg-surface shadow-2xl">
            {/* Modal header */}
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-hairline bg-surface px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-ink">{detail?.brand_name || 'Loading…'}</h2>
                {detail?.website_url && (
                  <a
                    href={detail.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-ink-muted hover:text-accent hover:underline"
                  >
                    {detail.website_url}
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="flex-shrink-0 rounded-lg border border-hairline p-1.5 text-ink-muted hover:bg-surface-page hover:text-ink"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal actions */}
            <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-5 py-3">
              {detail?.status === 'active' && (
                <button
                  type="button"
                  onClick={previewAsBrand}
                  disabled={previewing}
                  title="Open partner portal as this brand (1-hour session, fully audited)"
                  className="rounded-lg border border-accent/40 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
                >
                  {previewing ? 'Opening…' : '🔍 Preview as brand'}
                </button>
              )}
              {detail?.status === 'pending_review' && (
                <>
                  <button
                    type="button"
                    onClick={() => setRejecting(true)}
                    className="rounded-lg border border-status-critical/30 px-3 py-1.5 text-xs font-medium text-status-critical hover:bg-status-critical/10"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => setApproving(true)}
                    className="rounded-lg bg-raspberry-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-raspberry-700"
                  >
                    Approve &amp; queue crawl
                  </button>
                </>
              )}
            </div>

            <div className="p-5">
              {detailLoading ? (
                <p className="text-sm text-ink-muted">Loading…</p>
              ) : !detail ? (
                <p className="text-sm text-status-critical">Could not load details — try again.</p>
              ) : (
                <>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <Field label="Status"><StatusPill status={detail.status} /></Field>
                    <Field label="Crawl probe">
                      {detail.test_crawl_status ? <StatusPill status={detail.test_crawl_status} /> : '-'}
                    </Field>
                    <Field label="Domain" mono>{detail.brand_domain}</Field>
                    <Field label="Crawler">{detail.crawler_status}</Field>
                    <Field label="GST number" mono>{detail.gst_number || '-'}</Field>
                    <Field label="Plan">{detail.plan}</Field>
                    <Field label="Products indexed">{detail.products_indexed ?? 0}</Field>
                    <Field label="Applied">{formatDateTime(detail.created_at)}</Field>
                    <Field label="Signatory">
                      {detail.signatory_name
                        ? `${detail.signatory_name}${detail.signatory_designation ? ` · ${detail.signatory_designation}` : ''}`
                        : '-'}
                    </Field>
                    <Field label="Mobile" mono>{detail.signatory_phone || '-'}</Field>
                    <Field label="Contact emails">{(detail.contact_emails || []).join(', ') || '-'}</Field>
                    <Field label="Contact numbers">{(detail.contact_numbers || []).join(', ') || '-'}</Field>
                    {detail.address && (
                      <Field label="Address">
                        <Address address={detail.address} />
                      </Field>
                    )}
                    {detail.approved_by && <Field label="Approved by" mono>{detail.approved_by}</Field>}
                    {detail.rejection_reason && <Field label="Rejection reason">{detail.rejection_reason}</Field>}
                  </dl>

                  {detail.test_crawl_status === 'warning' && (
                    <p className="mt-4 rounded-lg bg-status-warning/10 px-3 py-2 text-xs leading-relaxed text-[#8a5a00]">
                      Crawl probe passed with warnings — expect a thinner first crawl than usual.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={approving}
        title={`Approve ${detail?.brand_name || 'this brand'}?`}
        body={
          <>
            <p>This activates their partner account and immediately queues a crawl against{' '}
              <span className="font-medium text-ink">{detail?.brand_domain}</span>.</p>
            <p className="mt-2">They will be able to sign in to partner.mahi.fashion and their products will
              start appearing in discovery once the crawl completes.</p>
          </>
        }
        confirmLabel="Approve and queue crawl"
        onConfirm={approve}
        onClose={() => setApproving(false)}
      />

      <ReasonDialog
        open={rejecting}
        title={`Reject ${detail?.brand_name || 'this application'}?`}
        body="The reason is stored on the account and written to the audit log. Write it for the person who reads it later, not for the form."
        confirmLabel="Reject application"
        tone="critical"
        onConfirm={reject}
        onClose={() => setRejecting(false)}
      />
    </div>
  );
}
